/**
 * Telegram inbound governance config, built from the per-agent env contract.
 *
 * ADR-0047: the opencode runtime's manager-side SenderGate must build the exact
 * same `PeerClassifierConfig` + chat allowlist the Claude Code telegram-channel
 * MCP builds, from the exact same env vars (Brad's option (a): the manager
 * materializes the identical env contract at spawn via `buildChannelServerEnv` /
 * the adapter's `writeChannelCredentials`). This module is the shared, pure home
 * for that env -> config parsing so the two runtimes cannot diverge on
 * correctness-critical gating.
 *
 * Faithful port of the module-level config in `packages/mcp/src/telegram-channel.ts`
 * (`PEER_CLASSIFIER_CONFIG` ~L314, `PEER_DISABLED_MODE` ~L333, `ALLOWED_CHATS`
 * ~L277), parameterized on an injected env map with `process.stderr.write`
 * replaced by an injected `onWarn` sink so core stays I/O-agnostic. Fail-closed
 * behaviour (a malformed kill-switch value biases to `'all'`) is preserved.
 *
 * NOTE (dedup follow-up): telegram-channel.ts still has its own inline copies of
 * this parsing at module scope. Until it is refactored to import these builders,
 * keep the two in sync - drift only mis-gates opencode agents (not the live Claude
 * fleet), but it is still a latent traffic-drop bug.
 */

import {
  parsePeerAgentModeEnv,
  parsePeerGroupIdsEnv,
  parsePeersEnv,
  type PeerClassifierConfig,
} from './telegram-peer-classifier.js';

/** A read-only view of the process env (or any equivalent map). */
export type EnvLike = Record<string, string | undefined>;

/**
 * Parse `TELEGRAM_ALLOWED_CHATS` (comma-separated chat ids) into a set. Empty /
 * unset ⇒ an empty set, which the gate treats as "no chat gating" (every chat
 * admitted at the org-boundary axis), matching the MCP's `ALLOWED_CHATS`.
 */
export function parseTelegramAllowedChatsEnv(env: EnvLike): Set<string> {
  return new Set(
    (env['TELEGRAM_ALLOWED_CHATS'] ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  );
}

export type TelegramPeerDisabledMode = 'off' | 'cross_team_only' | 'all';

/**
 * Parse the peer kill-switch. Reads the channel-agnostic `PEER_DISABLED` first,
 * falling back to the legacy boolean `TELEGRAM_PEER_DISABLED` so a child spawned
 * by an old manager still gates correctly. Fail-closed: a non-empty unrecognised
 * `PEER_DISABLED` biases to `'all'` (every peer surface short-circuited) rather
 * than silently re-enabling peer traffic. Faithful to the MCP's IIFE.
 */
export function parseTelegramPeerDisabledModeEnv(
  env: EnvLike,
  onWarn?: (msg: string) => void,
): TelegramPeerDisabledMode {
  const next = (env['PEER_DISABLED'] ?? '').trim().toLowerCase();
  if (next === 'off' || next === 'cross_team_only' || next === 'all') return next;
  if (next.length > 0) {
    onWarn?.(
      `invalid PEER_DISABLED=${JSON.stringify(env['PEER_DISABLED'])} - defaulting to 'all' (fail-closed)`,
    );
    return 'all';
  }
  const legacy = (env['TELEGRAM_PEER_DISABLED'] ?? '').trim().toLowerCase();
  return legacy === '1' || legacy === 'true' || legacy === 'yes' ? 'all' : 'off';
}

/**
 * Build the Telegram peer-agent classifier config from the per-agent env
 * contract. Thin composition over the pure `parse*` primitives that ship with
 * the peer classifier, plus the `PEER_DISABLED` kill-switch.
 */
export function buildTelegramPeerClassifierConfigFromEnv(
  env: EnvLike,
  opts?: { onWarn?: (msg: string) => void },
): PeerClassifierConfig {
  return {
    peer_agent_mode: parsePeerAgentModeEnv(env['TELEGRAM_PEER_AGENT_MODE']),
    peer_group_ids: parsePeerGroupIdsEnv(env['TELEGRAM_PEER_GROUP_IDS']),
    peers: parsePeersEnv(env['TELEGRAM_PEERS'], env['TELEGRAM_PEERS_GATE']),
    peer_disabled_mode: parseTelegramPeerDisabledModeEnv(env, opts?.onWarn),
  };
}
